import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Card,
  CardContent,
  Grid,
  SelectChangeEvent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ValidateIcon,
  Refresh as RefreshIcon,
  EventNote as CongeIcon,
  PictureAsPdf as PdfIcon,
  Assessment as RecapIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { congesAPI, employeesAPI, auditLogsAPI } from '../../../services/api';
import {
  Conge,
  TYPE_CONGE_OPTIONS,
  STATUT_CONGE_OPTIONS,
  getTypeCongeLabel,
  getStatutCongeLabel,
  getStatutCongeColor,
  RecapitulatifCongeResponse,
} from '../../../types/conge';
import { EmployeeDropdownItem } from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import usePdfPreview from '../../../hooks/usePdfPreview';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import CongeFormDialog from './CongeFormDialog';
import CongeValidationDialog from './CongeValidationDialog';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
// generateCongeRecuPDF is dynamically imported in handleGeneratePDF so the
// jspdf chunk only loads when the user requests a PDF.

const HR_COLOR = '#00897B';

const CONGE_FILTER_SCHEMA = {
  ref_conge:    { type: 'text',         param: 'ref_conge' },
  date_demande: { type: 'dateRange',    paramFrom: 'date_demande_from', paramTo: 'date_demande_to' },
  employee_id:  { type: 'autocomplete', param: 'employee_id', transform: (v: string) => Number(v) },
  type_conge:   { type: 'select',       param: 'type_conge' },
  date_debut:   { type: 'dateRange',    paramFrom: 'date_debut_from',   paramTo: 'date_debut_to' },
  date_fin:     { type: 'dateRange',    paramFrom: 'date_fin_from',     paramTo: 'date_fin_to' },
  nbr_jours:    { type: 'numberRange',  paramMin: 'nbr_jours_min',      paramMax: 'nbr_jours_max' },
  statut:       { type: 'select',       param: 'statut' },
} as const;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const CongesManagement: React.FC = () => {
  const [conges, setConges] = useState<Conge[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [anneeFilter, setAnneeFilter] = useState(new Date().getFullYear());
  const [tabValue, setTabValue] = useState(0);

  // Recap data
  const [recapData, setRecapData] = useState<RecapitulatifCongeResponse | null>(null);
  const [recapLoading, setRecapLoading] = useState(false);

  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [selectedConge, setSelectedConge] = useState<Conge | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [congeToDelete, setCongeToDelete] = useState<Conge | null>(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [congeToValidate, setCongeToValidate] = useState<Conge | null>(null);

  // Employees for dropdown
  const [employees, setEmployees] = useState<EmployeeDropdownItem[]>([]);

  const { showSuccess, showError } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    CONGE_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  // Stats
  const stats = {
    total: conges.length,
    enAttente: conges.filter(c => c.statut === 'EN_ATTENTE').length,
    validees: conges.filter(c => c.statut === 'VALIDEE').length,
    refusees: conges.filter(c => c.statut === 'REFUSEE').length,
  };

  const fetchConges = useCallback(async () => {
    setLoading(true);
    try {
      const response = await congesAPI.getAll({
        page: page + 1,
        size: rowsPerPage,
        search: search || undefined,
        annee: anneeFilter,
        ...toQueryParams(),
      });
      setConges(response.items);
      setTotalCount(response.total);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, anneeFilter, filters, showError]);

  const fetchEmployees = useCallback(async () => {
    try {
      const data = await employeesAPI.getAllForDropdown();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  const fetchRecapitulatif = useCallback(async () => {
    setRecapLoading(true);
    try {
      const data = await congesAPI.getRecapitulatif(anneeFilter);
      setRecapData(data);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setRecapLoading(false);
    }
  }, [anneeFilter, showError]);

  useEffect(() => {
    fetchConges();
  }, [fetchConges]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchRecapitulatif();
    }
  }, [tabValue, fetchRecapitulatif]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleAnneeChange = (event: SelectChangeEvent<number>) => {
    setAnneeFilter(event.target.value as number);
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddConge = () => {
    setSelectedConge(null);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleEditConge = (conge: Conge) => {
    setSelectedConge(conge);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (conge: Conge) => {
    setCongeToDelete(conge);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!congeToDelete) return;
    try {
      await congesAPI.delete(congeToDelete.id);
      showSuccess('Demande de congé supprimée avec succès');
      fetchConges();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setDeleteDialogOpen(false);
      setCongeToDelete(null);
    }
  };

  const handleValidateClick = (conge: Conge) => {
    setCongeToValidate(conge);
    setValidationDialogOpen(true);
  };

  const handleValidationSuccess = () => {
    fetchConges();
    if (tabValue === 1) {
      fetchRecapitulatif();
    }
  };

  const handleFormSuccess = () => {
    fetchConges();
    if (tabValue === 1) {
      fetchRecapitulatif();
    }
  };

  const handleGeneratePDF = (conge: Conge) => {
    openPreview({
      getBlob: async () => {
        const { generateCongeRecuPDF } = await import('../../../utils/congePdfGenerator');
        return generateCongeRecuPDF(conge, true) as Blob;
      },
      fileName: `demande_conge_${conge.ref_conge || conge.id}.pdf`,
    });
    auditLogsAPI.logPdfDownload('Conge', conge.id, `Aperçu PDF - ${conge.ref_conge}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Generate year options (from 2020 to current year + 1)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => 2020 + i);

  return (
    <Box sx={{ p: 0 }}>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: HR_COLOR, color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CongeIcon />
                <Typography variant="h6">{totalCount}</Typography>
              </Box>
              <Typography variant="body2">Total Demandes</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#FFA726', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6">{stats.enAttente}</Typography>
              <Typography variant="body2">En Attente</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#66BB6A', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6">{stats.validees}</Typography>
              <Typography variant="body2">Validées</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#EF5350', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6">{stats.refusees}</Typography>
              <Typography variant="body2">Refusées</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': { color: HR_COLOR },
            '& .Mui-selected': { color: HR_COLOR },
            '& .MuiTabs-indicator': { backgroundColor: HR_COLOR },
          }}
        >
          <Tab icon={<CongeIcon />} label="Demandes de Congés" iconPosition="start" />
          <Tab icon={<RecapIcon />} label="Récapitulatif Annuel" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Demandes de Congés */}
      <TabPanel value={tabValue} index={0}>
        <Paper sx={{ p: 2, mb: 2 }}>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Rechercher un employé..."
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Année</InputLabel>
              <Select value={anneeFilter} onChange={handleAnneeChange} label="Année">
                {yearOptions.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {hasActive() && (
              <Button
                size="small"
                startIcon={<FilterListOffIcon />}
                onClick={reset}
                sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                Effacer les filtres
              </Button>
            )}

            <Box sx={{ flexGrow: 1 }} />

            <Tooltip title="Actualiser">
              <IconButton onClick={fetchConges} sx={{ color: HR_COLOR }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddConge}
              sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}
            >
              Nouvelle Demande
            </Button>
          </Box>

          {/* Table */}
          <TableContainer sx={getModuleTableTheme('hr')}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Réf.
                      <ColumnFilter type="text" value={filters.ref_conge} onChange={(v) => update('ref_conge', v)} label="référence" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date Demande
                      <ColumnFilter type="dateRange" value={filters.date_demande} onChange={(v) => update('date_demande', v)} label="date demande" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Employé
                      <ColumnFilter
                        type="autocomplete"
                        value={filters.employee_id}
                        onChange={(v) => update('employee_id', v)}
                        label="employé"
                        color={HR_COLOR}
                        options={employees.map(e => ({ value: e.id.toString(), label: `${e.matricule || ''} ${e.nom_complet}`.trim() }))}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Type
                      <ColumnFilter
                        type="select"
                        value={filters.type_conge}
                        onChange={(v) => update('type_conge', v)}
                        label="type"
                        color={HR_COLOR}
                        options={TYPE_CONGE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Du
                      <ColumnFilter type="dateRange" value={filters.date_debut} onChange={(v) => update('date_debut', v)} label="date début" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Au
                      <ColumnFilter type="dateRange" value={filters.date_fin} onChange={(v) => update('date_fin', v)} label="date fin" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      Jours
                      <ColumnFilter type="numberRange" value={filters.nbr_jours} onChange={(v) => update('nbr_jours', v)} label="jours" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Statut
                      <ColumnFilter
                        type="select"
                        value={filters.statut}
                        onChange={(v) => update('statut', v)}
                        label="statut"
                        color={HR_COLOR}
                        options={STATUT_CONGE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">Chargement...</TableCell>
                  </TableRow>
                ) : conges.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">Aucune demande trouvée</TableCell>
                  </TableRow>
                ) : (
                  conges.map((conge) => (
                    <TableRow key={conge.id} hover>
                      <TableCell>
                        {conge.ref_conge ? (
                          <Chip
                            label={conge.ref_conge}
                            size="small"
                            sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(conge.date_demande)}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {conge.employee?.nom_complet || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {conge.employee?.matricule}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTypeCongeLabel(conge.type_conge)}
                          size="small"
                          sx={{ bgcolor: HR_COLOR, color: 'white' }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(conge.date_debut)}</TableCell>
                      <TableCell>{formatDate(conge.date_fin)}</TableCell>
                      <TableCell align="center">{conge.nbr_jours}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatutCongeLabel(conge.statut)}
                          size="small"
                          sx={{
                            bgcolor: getStatutCongeColor(conge.statut),
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {conge.statut === 'EN_ATTENTE' && (
                            <>
                              <Tooltip title="Valider/Refuser">
                                <IconButton
                                  size="small"
                                  onClick={() => handleValidateClick(conge)}
                                  sx={{ color: '#66BB6A' }}
                                >
                                  <ValidateIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Modifier">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditConge(conge)}
                                  sx={{ color: HR_COLOR }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {conge.statut === 'VALIDEE' && (
                            <Tooltip title="Générer PDF">
                              <IconButton
                                size="small"
                                onClick={() => handleGeneratePDF(conge)}
                                sx={{ color: '#1976D2' }}
                              >
                                <PdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(conge)}
                              sx={{ color: '#EF5350' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Lignes par page"
          />
        </Paper>
      </TabPanel>

      {/* Tab 1: Récapitulatif Annuel */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ color: HR_COLOR }}>
              Récapitulatif Congé Annuel (CP = Total cumulé)
            </Typography>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select value={anneeFilter} onChange={handleAnneeChange}>
                {yearOptions.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Actualiser">
              <IconButton onClick={fetchRecapitulatif} sx={{ color: HR_COLOR }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <TableContainer sx={getModuleTableTheme('hr')}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Matricule</TableCell>
                  <TableCell>Nom Complet</TableCell>
                  <TableCell>Fonction</TableCell>
                  <TableCell align="center">CP Acquis (Total)</TableCell>
                  <TableCell align="center">CP Pris (Total)</TableCell>
                  <TableCell align="center">CP Restant (Total)</TableCell>
                  <TableCell align="center">CM ({anneeFilter})</TableCell>
                  <TableCell align="center">CSS ({anneeFilter})</TableCell>
                  <TableCell align="center">Autres ({anneeFilter})</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recapLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">Chargement...</TableCell>
                  </TableRow>
                ) : !recapData || recapData.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">Aucune donnée disponible</TableCell>
                  </TableRow>
                ) : (
                  recapData.items.map((item) => (
                    <TableRow key={item.employee_id} hover>
                      <TableCell>{item.matricule}</TableCell>
                      <TableCell>{item.nom_complet}</TableCell>
                      <TableCell>{item.fonction || '-'}</TableCell>
                      <TableCell align="center">{item.cp_acquis}</TableCell>
                      <TableCell align="center">{item.cp_pris}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.cp_restant}
                          size="small"
                          sx={{
                            bgcolor: item.cp_restant <= 0 ? '#EF5350' : item.cp_restant <= 5 ? '#FFA726' : '#66BB6A',
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">{item.cm_pris}</TableCell>
                      <TableCell align="center">{item.css_pris}</TableCell>
                      <TableCell align="center">{item.autres_pris}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Form Dialog */}
      <CongeFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setFormMinimized(false);
        }}
        conge={selectedConge}
        employees={employees}
        onSuccess={handleFormSuccess}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      {/* Validation Dialog */}
      <CongeValidationDialog
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        conge={congeToValidate}
        onSuccess={handleValidationSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        type="warning"
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer la demande de congé de ${congeToDelete?.employee?.nom_complet || 'cet employé'} ?`}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        showCancel={true}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default CongesManagement;
